// src/pages/Signup.jsx
import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Custom Popup Component (same as before)
const CustomPopup = ({ message, type, onClose, onConfirm, showConfirm }) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '16px'
    }} onClick={onClose}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: 'clamp(20px, 5vw, 32px)',
        maxWidth: 'min(400px, 90vw)',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        animation: 'slideIn 0.3s ease',
        textAlign: 'center'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 'clamp(40px, 8vw, 60px)', marginBottom: '16px' }}>
          {type === 'success' ? '✅' : type === 'error' ? '❌' : type === 'warning' ? '⚠️' : 'ℹ️'}
        </div>
        <h4 style={{
          color: type === 'error' ? '#dc3545' : type === 'warning' ? '#f59e0b' : '#0d53bd',
          marginBottom: '16px',
          fontSize: 'clamp(16px, 4vw, 20px)',
          fontWeight: '600'
        }}>
          {type === 'error' ? 'Oops!' : type === 'warning' ? 'Warning' : type === 'success' ? 'Success!' : 'Notice'}
        </h4>
        <p style={{ color: '#555', marginBottom: '24px', fontSize: 'clamp(14px, 3vw, 16px)', lineHeight: '1.6' }}>
          {message}
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          {showConfirm ? (
            <>
              <button onClick={onConfirm} style={{ background: 'linear-gradient(135deg, #0d53bd, #06b6d4)', color: 'white', border: 'none', borderRadius: '30px', padding: 'clamp(10px, 2vw, 12px) clamp(20px, 4vw, 32px)', fontSize: 'clamp(14px, 3vw, 16px)', fontWeight: '600', cursor: 'pointer', flex: 1 }}>Yes</button>
              <button onClick={onClose} style={{ background: '#f3f4f6', color: '#666', border: 'none', borderRadius: '30px', padding: 'clamp(10px, 2vw, 12px) clamp(20px, 4vw, 32px)', fontSize: 'clamp(14px, 3vw, 16px)', fontWeight: '600', cursor: 'pointer', flex: 1 }}>No</button>
            </>
          ) : (
            <button onClick={onClose} style={{ background: 'linear-gradient(135deg, #0d53bd, #06b6d4)', color: 'white', border: 'none', borderRadius: '30px', padding: 'clamp(10px, 2vw, 12px) clamp(24px, 5vw, 40px)', fontSize: 'clamp(14px, 3vw, 16px)', fontWeight: '600', cursor: 'pointer', width: '100%' }}>OK</button>
          )}
        </div>
      </div>
      <style>{`@keyframes slideIn { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
};

export default function Signup() {
  const { sendOTP, verifyOTPAndSignup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [step, setStep] = useState('form');
  const [formData, setFormData] = useState({ email: '', username: '', password: '', confirmPassword: '' });
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  // Real-time validation states
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);

  // Error messages
  const [emailError, setEmailError] = useState('');
  const [usernameError, setUsernameError] = useState('');

  // ✅ OTP attempts state
  const [otpAttemptsLeft, setOtpAttemptsLeft] = useState(null);
  const [otpMessage, setOtpMessage] = useState('');

  // ✅ Redirect if already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');

    if (token && user) {
      // User is already logged in - redirect to home
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [navigate, location]);

  // Popup state
  const [popup, setPopup] = useState({ show: false, message: '', type: 'info', onConfirm: null, showConfirm: false });

  const showPopup = (message, type = 'info', showConfirm = false, onConfirm = null) => {
    setPopup({ show: true, message, type, showConfirm, onConfirm });
  };

  const hidePopup = () => {
    setPopup(prev => ({ ...prev, show: false }));
  };

  // Real-time email validation
  useEffect(() => {
    const checkEmail = async () => {
      const email = formData.email;
      if (!email || !email.includes('@') || email.length < 5) {
        setEmailAvailable(null);
        setEmailError('');
        return;
      }

      setCheckingEmail(true);
      setEmailError('');
      try {
        const response = await fetch('https://insightdraft-backend.onrender.com/api/auth/check-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        const data = await response.json();
        setEmailAvailable(data.available);
        if (!data.available) {
          setEmailError('This email is already registered. Please login instead.');
        }
      } catch (err) {
        console.error('Email check error:', err);
        setEmailAvailable(null);
      } finally {
        setCheckingEmail(false);
      }
    };

    const timer = setTimeout(checkEmail, 500);
    return () => clearTimeout(timer);
  }, [formData.email]);

  // Real-time username validation
  useEffect(() => {
    const checkUsername = async () => {
      const username = formData.username;
      if (!username || username.length < 3) {
        setUsernameAvailable(null);
        setUsernameError('');
        return;
      }

      if (!/^[a-zA-Z0-9_]+$/.test(username)) {
        setUsernameAvailable(false);
        setUsernameError('Username can only contain letters, numbers, and underscores.');
        return;
      }

      if (username.length < 3 || username.length > 20) {
        setUsernameAvailable(false);
        setUsernameError('Username must be 3–20 characters.');
        return;
      }

      setCheckingUsername(true);
      setUsernameError('');
      try {
        const response = await fetch('https://insightdraft-backend.onrender.com/api/auth/check-username', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username })
        });
        const data = await response.json();
        setUsernameAvailable(data.available);
        if (!data.available) {
          setUsernameError('This username is already taken. Please choose another.');
        }
      } catch (err) {
        console.error('Username check error:', err);
        setUsernameAvailable(null);
      } finally {
        setCheckingUsername(false);
      }
    };

    const timer = setTimeout(checkUsername, 500);
    return () => clearTimeout(timer);
  }, [formData.username]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');

    const { email, username, password, confirmPassword } = formData;

    if (!email || !username || !password || !confirmPassword) {
      showPopup('All fields are required.', 'error');
      return;
    }
    if (password !== confirmPassword) {
      showPopup('Passwords do not match.', 'error');
      return;
    }
    if (password.length < 8) {
      showPopup('Password must be at least 8 characters.', 'error');
      return;
    }
    if (emailAvailable === false) {
      showPopup('This email is already registered. Please login instead.', 'error');
      return;
    }
    if (usernameAvailable === false) {
      showPopup('This username is already taken. Please choose another.', 'error');
      return;
    }

    setPageLoading(true);
    try {
      const response = await sendOTP(email, username, password);

      // ✅ Store OTP attempts info from response
      if (response.attemptsLeft !== undefined) {
        setOtpAttemptsLeft(response.attemptsLeft);
        setOtpMessage(`You have ${response.attemptsLeft} OTP attempt${response.attemptsLeft !== 1 ? 's' : ''} left today.`);
      }

      showPopup(`OTP sent to ${email}! Valid for 10 minutes.`, 'success');
      setStep('otp');
    } catch (err) {
      showPopup(err.message, 'error');
    } finally {
      setPageLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');

    if (!otp || otp.length !== 6) {
      showPopup('Please enter the 6-digit OTP.', 'error');
      return;
    }

    setPageLoading(true);
    try {
      await verifyOTPAndSignup(formData.email, formData.username, formData.password, otp);
      showPopup('Account created successfully! Redirecting to login...', 'success');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      showPopup(err.message, 'error');
    } finally {
      setPageLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResendLoading(true);
    setError('');
    try {
      const response = await sendOTP(formData.email, formData.username, formData.password);

      // ✅ Update OTP attempts info
      if (response.attemptsLeft !== undefined) {
        setOtpAttemptsLeft(response.attemptsLeft);
        setOtpMessage(`You have ${response.attemptsLeft} OTP attempt${response.attemptsLeft !== 1 ? 's' : ''} left today.`);
      }

      showPopup('New OTP sent!', 'success');
    } catch (err) {
      showPopup(err.message, 'error');
    } finally {
      setResendLoading(false);
    }
  };

  // Full page loading spinner
  if (pageLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} />
          <h5 className="text-primary" style={{ fontSize: '20px' }}>
            {step === 'form' ? 'Sending OTP...' : 'Creating your account...'}
          </h5>
          <p className="text-muted mt-2">Please wait a moment</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Popup Modal */}
      {popup.show && (
        <CustomPopup
          message={popup.message}
          type={popup.type}
          onClose={hidePopup}
          onConfirm={() => {
            if (popup.onConfirm) popup.onConfirm();
            hidePopup();
          }}
          showConfirm={popup.showConfirm}
        />
      )}

      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.logo}>🚀 InsightDraft AI</h1>
          <h2 style={styles.title}>{step === 'form' ? 'Create Account' : 'Verify Email'}</h2>
          {step === 'otp' && (
            <>
              <p style={styles.subtitle}>Enter the 6-digit OTP sent to <strong>{formData.email}</strong></p>
              {/* ✅ OTP Attempts Display */}
              {otpAttemptsLeft !== null && (
                <div style={{
                  ...styles.otpInfo,
                  color: otpAttemptsLeft <= 2 ? '#dc3545' : '#0d53bd'
                }}>
                  <span>🪙</span> {otpMessage}
                </div>
              )}
            </>
          )}
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        {step === 'form' ? (
          <form onSubmit={handleSendOTP} style={styles.form}>
            {/* Email Field */}
            <div style={styles.fieldContainer}>
              <div style={{ position: 'relative' }}>
                <input
                  style={{
                    ...styles.input,
                    borderColor: emailAvailable === true ? '#10b981' : emailAvailable === false ? '#dc3545' : '#e0e7ff'
                  }}
                  type="email"
                  name="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                {checkingEmail && (
                  <span style={styles.inputIcon}>
                    <span className="spinner-border spinner-border-sm" style={{ width: '16px', height: '16px', color: '#666' }} />
                  </span>
                )}
                {emailAvailable === true && !checkingEmail && (
                  <span style={{ ...styles.inputIcon, color: '#10b981' }}>✓</span>
                )}
                {emailAvailable === false && !checkingEmail && (
                  <span style={{ ...styles.inputIcon, color: '#dc3545' }}>✗</span>
                )}
              </div>
              {emailError && (
                <div style={styles.fieldError}>
                  <span style={{ marginRight: '4px' }}>⚠️</span> {emailError}
                </div>
              )}
            </div>

            {/* Username Field */}
            <div style={styles.fieldContainer}>
              <div style={{ position: 'relative' }}>
                <input
                  style={{
                    ...styles.input,
                    borderColor: usernameAvailable === true ? '#10b981' : usernameAvailable === false ? '#dc3545' : '#e0e7ff'
                  }}
                  type="text"
                  name="username"
                  placeholder="Username (3–20 chars, letters/numbers/_)"
                  value={formData.username}
                  onChange={handleChange}
                  required
                />
                {checkingUsername && (
                  <span style={styles.inputIcon}>
                    <span className="spinner-border spinner-border-sm" style={{ width: '16px', height: '16px', color: '#666' }} />
                  </span>
                )}
                {usernameAvailable === true && !checkingUsername && (
                  <span style={{ ...styles.inputIcon, color: '#10b981' }}>✓</span>
                )}
                {usernameAvailable === false && !checkingUsername && (
                  <span style={{ ...styles.inputIcon, color: '#dc3545' }}>✗</span>
                )}
              </div>
              {usernameError && (
                <div style={styles.fieldError}>
                  <span style={{ marginRight: '4px' }}>⚠️</span> {usernameError}
                </div>
              )}
            </div>

            {/* Password Field */}
            <div style={styles.fieldContainer}>
              <div style={{ position: 'relative' }}>
                <input
                  style={{
                    ...styles.input,
                    paddingRight: '44px'
                  }}
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password (min 8 characters)"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    ...styles.eyeIcon,
                    cursor: 'pointer'
                  }}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    // 👁️ Eye Open Icon
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  ) : (
                    // 👁️‍🗨️ Eye Closed Icon
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  )}
                </span>
              </div>
            </div>

            {/* 🆕 Confirm Password Field with Eye Icon */}
            <div style={styles.fieldContainer}>
              <div style={{ position: 'relative' }}>
                <input
                  style={{
                    ...styles.input,
                    paddingRight: '44px',
                    borderColor: formData.password && formData.confirmPassword
                      ? (formData.password === formData.confirmPassword ? '#10b981' : '#dc3545')
                      : '#e0e7ff'
                  }}
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
                <span
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    ...styles.eyeIcon,
                    cursor: 'pointer'
                  }}
                  title={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? (
                    // 👁️ Eye Open Icon
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  ) : (
                    // 👁️‍🗨️ Eye Closed Icon
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  )}
                </span>
              </div>
              {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <div style={styles.fieldError}>
                  <span style={{ marginRight: '4px' }}>⚠️</span> Passwords do not match
                </div>
              )}
            </div>

            <button style={styles.btn} type="submit" disabled={loading}>
              {loading ? 'Sending OTP...' : 'Send OTP & Continue'}
            </button>

            <div style={styles.divider}><span>or</span></div>

            <button type="button" style={styles.googleBtn} onClick={loginWithGoogle}>
              <GoogleIcon /> Continue with Google
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP} style={styles.form}>
            <input
              style={{ ...styles.input, textAlign: 'center', letterSpacing: '8px', fontSize: '24px', fontWeight: 'bold' }}
              type="text"
              placeholder="000000"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              required
            />
            <button style={styles.btn} type="submit" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify & Create Account'}
            </button>
            <button
              type="button"
              style={{ ...styles.btn, background: 'transparent', color: '#0d53bd', border: '2px solid #0d53bd', marginTop: '8px' }}
              onClick={handleResendOTP}
              disabled={resendLoading}
            >
              {resendLoading ? 'Sending...' : 'Resend OTP'}
            </button>
            <button
              type="button"
              style={{ ...styles.btn, background: 'transparent', color: '#888', border: 'none', fontSize: '13px', marginTop: '4px' }}
              onClick={() => { setStep('form'); setError(''); }}
            >
              ← Back to form
            </button>
          </form>
        )}

        <p style={styles.loginLink}>
          Already have an account? <Link to="/login" style={{ color: '#0d53bd', fontWeight: '600' }}>Login</Link>
        </p>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" style={{ marginRight: 8, verticalAlign: 'middle' }}>
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.9 7.2v6h7.9c4.6-4.3 7.3-10.6 7.3-17.2z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.9-6c-2.1 1.4-4.8 2.3-8 2.3-6.1 0-11.3-4.1-13.2-9.7H2.7v6.2C6.7 42.7 14.8 48 24 48z" />
      <path fill="#FBBC05" d="M10.8 28.8c-.5-1.4-.7-2.9-.7-4.8s.3-3.3.7-4.8v-6.2H2.7C1 16.5 0 20.1 0 24s1 7.5 2.7 10.9l8.1-6.1z" />
      <path fill="#EA4335" d="M24 9.5c3.4 0 6.5 1.2 8.9 3.5l6.6-6.6C35.9 2.4 30.4 0 24 0 14.8 0 6.7 5.3 2.7 13.1l8.1 6.2C12.7 13.6 17.9 9.5 24 9.5z" />
    </svg>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)',
  },
  card: {
    background: 'white',
    borderRadius: '24px',
    padding: '40px',
    width: '100%',
    maxWidth: '440px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
  },
  header: { textAlign: 'center', marginBottom: '28px' },
  logo: { margin: 0, fontSize: '28px', color: '#0d53bd' },
  title: { margin: '12px 0 4px', fontSize: '24px', color: '#333', fontWeight: '700' },
  subtitle: { color: '#666', fontSize: '14px', margin: 0 },
  otpInfo: {
    fontSize: '13px',
    marginTop: '8px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '8px 12px',
    background: '#f0f7ff',
    borderRadius: '20px',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '8px' },
  fieldContainer: { marginBottom: '4px' },
  input: {
    padding: '14px 40px 14px 16px',
    border: '2px solid #e0e7ff',
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
    transition: 'border-color 0.3s',
    backgroundColor: '#fafbff',
    width: '100%',
  },
  inputIcon: {
    position: 'absolute',
    right: '14px',
    top: '14px',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  fieldError: {
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '4px',
    marginLeft: '4px',
    display: 'flex',
    alignItems: 'center',
  },
  btn: {
    padding: '14px',
    background: 'linear-gradient(135deg, #0e4aa5ff, #06b6d4)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '8px',
  },
  googleBtn: {
    padding: '14px',
    background: 'white',
    color: '#333',
    border: '2px solid #e0e7ff',
    borderRadius: '12px',
    fontSize: '15px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    fontWeight: '500',
  },
  eyeIcon: {
    position: 'absolute',
    right: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    cursor: 'pointer',
    color: '#888',
    transition: 'color 0.2s',
  },
  divider: { textAlign: 'center', color: '#aaa', fontSize: '13px', margin: '8px 0' },
  errorBox: { background: '#fff0f0', border: '1px solid #ffcdd2', color: '#c62828', padding: '12px 14px', borderRadius: '12px', fontSize: '14px', marginBottom: '16px' },
  loginLink: { textAlign: 'center', marginTop: '20px', color: '#666', fontSize: '14px' },
};